import { AxiosResponse } from "axios";
import { Contact, ContactsList } from "@/models/contact";
import axios from "@/services/axios";
import { setExportFileHeaders } from "@/helpers/utilities";

class ContactsService {
  getContacts(
    page: number,
    size: number,
    contactFolderId: string,
    searchText?: string,
    sortField?: string,
    sortOrder?: string,
    selectionType?: string,
  ): Promise<AxiosResponse<ContactsList>> {
    const dir =
      sortOrder === "descend" ? sortField + ",DESC" : sortField + ",ASC";
    const sort = sortField && sortOrder != "none" ? dir : "";
    return axios.get(`/api/contacts/${contactFolderId}`, {
      params: {
        page,
        size,
        sort,
        filter: searchText,
        selectionType: selectionType ?? "ALL",
      },
    });
  }

  updateContact(contact: Contact, contactFolderId: string | undefined) {
    return axios.put(`/api/contacts/${contactFolderId}/contact`, contact);
  }

  createContact(contact: Contact, contactFolderId: string | undefined) {
    return axios.post(`/api/contacts/${contactFolderId}/contact`, contact);
  }

  deleteContact(contactId: string | undefined) {
    return axios.delete(`/api/contacts/${contactId}/contact`);
  }

  addFavouriteContact(contact: any): Promise<AxiosResponse> {
    return axios.put(`/api/contacts/${contact.contactId}/favourite`, {
      favourite: contact.favourite,
    });
  }

  exportContacts(
    contactFolderId: string,
    fileTypes: string,
    withUUID: boolean = false,
  ): Promise<AxiosResponse> {
    const { headers, responseType } = setExportFileHeaders(fileTypes);
    return axios.get(`/api/contacts/${contactFolderId}/export`, {
      params: {
        fileTypes,
        withUUID,
      },
      headers,
      responseType,
    });
  }

  importContacts(
    contactFolderId: string | undefined,
    file: File,
    fileType: string,
    onUploadProgress: any,
  ): Promise<AxiosResponse> {
    const formData = new FormData();
    formData.append("file", file);

    return axios.post(`/api/contacts/${contactFolderId}/import`, formData, {
      params: {
        fileType,
      },
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress,
    });
  }

  getFilteredContacts(
    page: number,
    size: number,
    contactFolderId: string,
    searchString?: string,
    sortField?: string,
    sortOrder?: string,
  ): Promise<AxiosResponse<ContactsList>> {
    const sort =
      sortOrder && sortOrder === "descend"
        ? sortField + ",DESC"
        : sortField + ",ASC";
    return axios.get(`/api/contacts/${contactFolderId}/filter-search`, {
      params: {
        page,
        size,
        sort,
        search: searchString,
      },
    });
  }

  getSubscribedGroupsForContact(contactId?: string): Promise<AxiosResponse> {
    return axios.get(`/api/contact-groups/${contactId}/subscription`);
  }

  addSubscriptionForContact(
    contactId?: string,
    contactGroupId?: string,
  ): Promise<AxiosResponse> {
    return axios.post(
      `/api/contact-groups/${contactGroupId}/member/${contactId}`,
    );
  }

  deleteSubscriptionForContact(contactId?: string, contactGroupId?: string) {
    return axios.delete(
      `/api/contact-groups/${contactGroupId}/member/${contactId}`,
    );
  }

  updateAddressSelection(
    contactId?: string,
    primaryAddress?: string,
    secondaryAddress?: string,
  ) {
    return axios.put(`/api/contacts/${contactId}/address-selection`, {
      primaryAddress,
      secondaryAddress,
    });
  }

  getContactByContactId(contactId: string): Promise<AxiosResponse<Contact>> {
    return axios.get(`/api/contacts/${contactId}/contact`);
  }

  getContactListColumns(contactFolderId: string) {
    return axios.get(`/api/contacts/column-display/${contactFolderId}`);
  }

  updateContactListColumns(contactFolderId: string, payload: any) {
    return axios.put(
      `/api/contacts/column-display/${contactFolderId}`,
      payload,
    );
  }

  resetContactListColumns(contactFolderId: string) {
    return axios.delete(`/api/contacts/column-display/${contactFolderId}`);
  }

  getMetaData(contactFolderId: string) {
    return axios.get(`/api/contacts/metadata/${contactFolderId}`);
  }

  validateHeaderMapping(contactFolderId: string | undefined, file: File) {
    const formData = new FormData();
    formData.append("file", file);

    return axios.post(
      `/api/contacts/${contactFolderId}/validate-header-mapping`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
  }

  deleteFile(fileId: string) {
    return axios.delete(`/api/contacts/cancel-import/${fileId}`);
  }

  updateHeaderMapping(contactFolderId: string | undefined, payload: any) {
    return axios.put(
      `/api/contacts/contact-folder/${contactFolderId}/header-mappings`,
      payload,
    );
  }

  validateData(
    contactFolderId: string | undefined,
    fileId: string | undefined,
  ) {
    return axios.post(
      `/api/contacts/${contactFolderId}/validate-data/${fileId}`,
    );
  }

  downloadErrorFile(fileId: string | undefined, fileTypes: string) {
    const { headers, responseType } = setExportFileHeaders(fileTypes);
    return axios.get(`/api/contacts/download-error-file/${fileId}`, {
      headers,
      responseType,
    });
  }

  finalImport(
    contactFolderId: string | undefined,
    fileId: string | undefined,
    importWithoutError: boolean,
  ) {
    return axios.post(
      `/api/contacts/${contactFolderId}/import/${fileId}/${importWithoutError}`,
    );
  }

  createMetaData(contactFolderId: string, paylod: any) {
    return axios.post(
      `/api/contacts/metadata/${contactFolderId}/metadata-column`,
      paylod,
    );
  }

  updateMetaData(contactFolderId: string, paylod: any) {
    return axios.put(
      `/api/contacts/metadata/${contactFolderId}/metadata-column`,
      paylod,
    );
  }

  deleteMetaData(metadataId: string) {
    return axios.delete(`/api/contacts/metadata/${metadataId}`);
  }

  deleteAllContacts(contactFolderId: string | undefined, username: string) {
    return axios.delete(
      `/api/contacts/${contactFolderId}/deleteAll?key=${username}`,
    );
  }
}
export const contactService = new ContactsService();
